import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { auth } from "../../firebase.js";
import WorkspaceSidebar from "./WorkspaceSidebar";
import WorkspaceComments from "./WorkspaceComments.js";
import TaskDetailModal from "../shared/TaskDetailModal";
import useTaskDetailModal from "../../utils/useTaskDetailModal";
import SaveAsTemplateDialog from "../shared/templates/SaveAsTemplateDialog";
import "../../css/Document.css";
import "../../css/DocumentationMainPage.css";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css"
import "../../css/DocumentationHomeComponent.css";
import "../../css/TeamWorkspaceMainPage.css";
import "../../css/TaskReference.css";
import "../../css/DocViewerMakeover.css";
import { downloadAsMarkdown, downloadAsJSON, downloadBulkAsZip, exportWorkspacePdf, loadPdfOptions, savePdfOptions } from "../../utils/exportUtils";
import PdfExportOptions from "../shared/PdfExportOptions";
import { wrapTablesForViewing } from "../../utils/contentTransforms";
import SpaceTableOfContents from "../shared/SpaceTableOfContents";

function WorkspaceDocument() {
    const { customDomain, type, id } = useParams();
    const [document, setDocument] = useState(null);
    const [workspace, setWorkspace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const { enhancedRightClickEnabled } = useQuickLinks();
    const [pinnedId, setPinnedId] = useState(null);
    const [canPin, setCanPin] = useState(false);
    const [rawContent, setRawContent] = useState("");
    const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
    const [authResolved, setAuthResolved] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [workspaceTheme, setWorkspaceTheme] = useState(null)
    const [isFavorited, setIsFavorited] = useState(false);
    const [favoriteId, setFavoriteId] = useState(null);
    const [ownerIsSubscriber, setOwnerIsSubscriber] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Authenticating user...");
    const [isShareVisible, setIsShareVisible] = useState(false);
    const [exportModal, setExportModal] = useState(null); // { format: 'markdown'|'json'|'pdf' }
    const [exportScope, setExportScope] = useState('current');
    const [exportLoading, setExportLoading] = useState(false);
    // PDF export (BBD-119): options are remembered between exports; status
    // carries the progress / error / "we'll notify you" line in the modal.
    const [pdfOptions, setPdfOptions] = useState(loadPdfOptions);
    const [pdfStatus, setPdfStatus] = useState(null);
    const navigate = useNavigate();
    const { activeTask, open: openTaskModal, close: closeTaskModal } = useTaskDetailModal();

    useEffect(() => {
        setLoadingMessage("Authenticating user...");
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserInfo(user);
            } else {
                setUserInfo(null);
            }
            setAuthResolved(true);
        });
        
        return () => unsubscribe();
    }, []);

  const loggedOutOptions = [
    {
      label: "Login",
      icon: <i className="fas fa-sign-in-alt"></i>,
      url: "/login",
      onClick: () => navigate("/login"),
    },
    {
      label: "Register",
      icon: <i className="fas fa-user-plus"></i>,
      url: "/register",
      onClick: () => navigate("/register"),
    },
    {
      label: "Try My Notebook",
      icon: <i className="fas fa-book"></i>,
      url: "/my-notebook",
      onClick: () => navigate("/my-notebook"),
    },
    {
      label: "Visit YouTube Channel",
      icon: <i className="fab fa-youtube"></i>,
      url: "https://www.youtube.com/@schoolofintology4062",
      onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
    },
    {
      label: "Join Discord",
      icon: <i className="fab fa-discord"></i>,
      url: "https://discord.gg/DYkESHwgVA",
      onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
    },
  ];


const loggedInOptions = userInfo && userInfo.uid ? [
            {
                label: "Home",
                icon: <i className="fas fa-home"></i>,
                url: "/",
                onClick: () => navigate("/"),
            },
    {
        label: "Your Documents",
        url: "/documentation",
        onClick: () => navigate("/documentation"),
    },
    {
        label: "Your Walkthroughs",
        url: "/walkthroughs",
        onClick: () => navigate("/walkthroughs"),
    },
    {
        label: "Your Notebook",
        url: "/my-notebook",
        onClick: () => navigate("/my-notebook"),
    },
    {
        label: "Your Workspace",
        url: "/workspaces",
        onClick: () => navigate("/workspaces"),
    },
    {
        label: "Account Settings",
        url: `/account/${userInfo.uid}`,
        onClick: () => navigate(`/account/${userInfo.uid}`),
    },
] : [];

// Use loggedInOptions if signed in, otherwise loggedOutOptions
const menuOptions = userInfo && userInfo.uid ? loggedInOptions : loggedOutOptions;
const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

    useEffect(() => {
        if (userInfo && workspace && document) {
            checkIfPinned();
            const userRole = workspace.user_role;
            setCanPin(['OWNER', 'ADMIN'].includes(userRole));
        }
    }, [userInfo, workspace, document]);

    useEffect(() => {
        if (userInfo && workspace && document) {
            checkIfFavorited();
        }
    }, [userInfo, workspace, document]);


    // Theme is applied globally through the --bb-* tokens (BBD-75), so the
    // page no longer waits on a per-page theme flag before rendering.
    useEffect(() => {
        if (document) {
            setLoading(false);
        }
    }, [document]);

      

    useEffect(() => {
        if (!authResolved) return;
        
        if (authResolved && !userInfo) {
            setError("You must be logged in to view this document");
            setLoading(false);
            return;
        }
        
        const abortController = new AbortController();
        const signal = abortController.signal;
        
        const fetchWorkspaceByCustomDomain = async () => {
            setLoadingMessage("Fetching workspace details...");
            try {
                const token = await userInfo.getIdToken();
                
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/domain/${customDomain}/`, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `${token}`,
                        'Auth-Token': `Bearer ${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    },
                    signal: signal
                });
    
                if (!response.ok) {
                    throw new Error(`Failed to fetch workspace: ${response.status}`);
                }
    
                const workspaceData = await response.json();
                setWorkspace(workspaceData);
                setWorkspaceTheme(workspaceData.theme);
                setOwnerIsSubscriber(workspaceData.owner.is_subscriber);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error("Error fetching workspace:", error);
                    setError("Error fetching workspace details");
                    setLoading(false);
                }
            }
        };
    
        if (customDomain && userInfo) {
            fetchWorkspaceByCustomDomain();
        }
    
        return () => {
            abortController.abort();
        };
    }, [customDomain, authResolved, userInfo]);

    useEffect(() => {
        if (!authResolved || !userInfo) return;
        
        if (type === 'create' && id === 'document') {
            return;
        }
        
        const fetchDocumentDetails = async () => {
            setLoadingMessage(`Fetching ${type} details...`);
            if (!workspace || !workspace.id) return;
            
            try {
                const token = await userInfo.getIdToken();
                let endpoint;
                
                if (type === 'space') {
                    endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/${id}/`;
                } else if (type === 'page') {
                    endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${id}/`;
                } else {
                    throw new Error(`Invalid document type: ${type}`);
                }
                
                const response = await fetch(endpoint, {
                    method: "GET",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': userInfo.uid,
                        'Requesting-Type': 'view'
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch document: ${response.status}`);
                }

                const documentData = await response.json();
                
                let canEditValue = false;

                if (workspace.user_role && ['OWNER', 'ADMIN', 'EDITOR'].includes(workspace.user_role)) {
                    canEditValue = true;
                }

                // Check if user is a viewer and document is not published
                if (workspace.user_role === 'VIEWER' && !documentData.completed) {
                    setError("You don't have permission to view this unpublished document");
                    setLoading(false);
                    return;
                }

                setCanEdit(canEditValue);
                // Wide tables scroll inside their own container instead of
                // extending past the document card's right edge (BBD-71).
                setRawContent(documentData.content || "");
                setDocument({ ...documentData, content: wrapTablesForViewing(documentData.content) });
            } catch (error) {
                console.error(`Error fetching ${type} details:`, error);
                setError(`Error fetching document details: ${error.message}`);
                setLoading(false);
            }
        };

        if (workspace && id && type) {
            fetchDocumentDetails();
        }

    }, [workspace, id, type, authResolved, userInfo]); 

    useEffect(() => {
        if (!authResolved || !customDomain || !id || !type) return;
        
        if (type === 'create' && id === 'document') {
            return;
        }
        
        // Only check public document if not logged in OR if logged in but not authorized (workspace/document fetch failed)
        if (!userInfo || error) {
            const checkPublicDocument = async () => {
                setLoadingMessage("Checking if this document is publicly accessible...");
                setLoading(true); // Keep loading screen visible while checking
                try {
                    const publicResponse = await fetch(
                        `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/public/documents/${customDomain}/${id}/`,
                        {
                            method: "GET",
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        }
                    );

                    if (publicResponse.ok) {
                        // Document is public, redirect to public URL
                        const publicData = await publicResponse.json();
                        const docName = publicData.document_type === 'space' ? publicData.name : publicData.page_name;
                        const publicUrl = `https://public.bluebird-documentation.com/docs/team_${publicData.document_type}/${id}/${encodeURIComponent(docName)}?workspace_id=${publicData.workspace_id}&doc_id=${id}`;
                        window.location.href = publicUrl;
                    } else {
                        // Document is private or not found - error is already set
                        if (!error) {
                            setError("You must be logged in to view this document");
                        }
                        setLoading(false);
                    }
                } catch (error) {
                    console.error("Error checking public document:", error);
                    setError("You must be logged in to view this document");
                    setLoading(false);
                }
            };

            // Only call if we haven't already checked (avoid infinite loops)
            if (!document && !workspace) {
                checkPublicDocument();
            }
        }
    }, [authResolved, userInfo, customDomain, id, type, error, document, workspace]); 

    const checkIfPinned = async () => {
        try {
            if (!userInfo || !workspace) return;
            
            const token = await userInfo.getIdToken();
            
            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pinned/`, {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                    'Auth-Token': `Bearer ${token}`,
                    'Authorization': `${token}`,
                    'Requesting-User-Uid': userInfo.uid
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to fetch pinned documents");
            }
            
            const pinnedDocuments = await response.json();
            
            const pinned = pinnedDocuments.find(pin => 
                pin.document_type === type && 
                String(pin.document_id) === String(id)
            );
            
            if (pinned) {
                setIsPinned(true);
                setPinnedId(pinned.id);
            } else {
                setIsPinned(false);
                setPinnedId(null);
            }
        } catch (error) {
            console.error("Error checking if document is pinned:", error);
        }
    };

    const togglePin = async () => {
        try {
            if (!userInfo) {
                alert("You must be logged in to pin documents");
                return;
            }
            
            if (!canPin) {
                alert("You don't have permission to pin documents in this workspace");
                return;
            }
            
            const token = await userInfo.getIdToken();
            
            if (isPinned) {
                // Remove from pinned documents
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pinned/${pinnedId}/remove/`, {
                    method: "DELETE",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    }
                });
                
                if (response.ok) {
                    setIsPinned(false);
                    setPinnedId(null);
                } else {
                    console.error("Failed to unpin document:", await response.json());
                    alert("Failed to unpin document. Please try again.");
                }
            } else {
                // Add to pinned documents
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pinned/add/`, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `${token}`,
                        'Auth-Token': `Bearer ${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    },
                    body: JSON.stringify({
                        document_type: type,
                        document_id: id,
                    })
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setIsPinned(true);
                    if (data.id) {
                        setPinnedId(data.id);
                    }
                } else {
                    console.error("Failed to pin document:", await response.json());
                    alert("Failed to pin document. Please try again.");
                }
            }
        } catch (error) {
            console.error("Error toggling pin status:", error);
            alert("An error occurred. Please try again.");
        }
    };

    const checkIfFavorited = async () => {
        try {
            if (!userInfo) return;
            
            const token = await userInfo.getIdToken();
            
            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/favorites/get-favorites/`, {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                    'Auth-Token': `Bearer ${token}`,
                    'Authorization': `${token}`,
                    'Requesting-User-Uid': userInfo.uid
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to fetch user favorites");
            }
            
            const favorites = await response.json();
            
            const favorite = favorites.find(fav => 
                fav.document_type === type && 
                String(fav.document_id) === String(id)
            );
            
            if (favorite) {
                setIsFavorited(true);
                setFavoriteId(favorite.id);
            } else {
                setIsFavorited(false);
                setFavoriteId(null);
            }
        } catch (error) {
            console.error("Error checking if document is favorited:", error);
        }
    };

    const toggleFavorite = async () => {
        try {
            if (!userInfo) {
                alert("You must be logged in to favorite documents");
                return;
            }
            
            const token = await userInfo.getIdToken();
            
            if (isFavorited) {
                // Remove from favorites
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/favorites/remove/${favoriteId}/`, {
                    method: "DELETE",
                    headers: {
                        'Content-Type': 'application/json',
                        'Auth-Token': `Bearer ${token}`,
                        'Authorization': `${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    }
                });
                
                if (response.ok) {
                    setIsFavorited(false);
                    setFavoriteId(null);
                }
            } else {
                // Add to favorites
                const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/favorites/add/`, {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `${token}`,
                        'Auth-Token': `Bearer ${token}`,
                        'Requesting-User-Uid': userInfo.uid
                    },
                    body: JSON.stringify({
                        document_type: type,
                        document_id: id,
                        workspace_id: workspace.id
                    })
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setIsFavorited(true);
                    if (data.favorite_id) {
                        setFavoriteId(data.favorite_id);
                    }
                }
            }
        } catch (error) {
            console.error("Error toggling favorite status:", error);
        }
    };

    const handleDeleteDocument = async () => {
        const warningMessage = type === 'space' 
            ? "Are you sure you want to delete this space?\n\n-This action cannot be undone.\n-Any pages within this space will be deleted as well."
            : "Are you sure you want to delete this page?\n\n-This action cannot be undone.";
            
        const isConfirmed = window.confirm(warningMessage);
        if (!isConfirmed) {
            return;
        }
        
        try {
            if (!userInfo) {
                setError("You must be logged in to delete this document");
                return;
            }

            const token = await userInfo.getIdToken();
            let endpoint;
            
            if (type === 'space') {
                endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/${id}/delete/`;
            } else if (type === 'page') {
                endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${id}/delete/`;
            } else {
                throw new Error(`Invalid document type: ${type}`);
            }
            
            const response = await fetch(endpoint, {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `${token}`,
                    'Auth-Token': `Bearer ${token}`,
                    'Requesting-User-Uid': userInfo.uid
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to delete document: ${response.status}`);
            }

            navigate(`/workspace/${customDomain}`);
        } catch (error) {
            console.error(`Error deleting ${type}:`, error);
            setError(`Error deleting document: ${error.message}`);
        }
    };

    const handleArchiveDocument = async () => {
        const isCurrentlyArchived = document?.is_archived;
        const action = isCurrentlyArchived ? 'unarchive' : 'archive';
        const confirmMsg = isCurrentlyArchived
            ? `Unarchive this ${type}?`
            : `Archive this ${type}? All child documents will also be archived.`;
        if (!window.confirm(confirmMsg)) return;
        try {
            if (!userInfo) { setError("You must be logged in."); return; }
            const token = await userInfo.getIdToken();
            const endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/${type === 'space' ? 'spaces' : 'pages'}/${id}/${action}/`;
            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `${token}`,
                    'Auth-Token': `Bearer ${token}`,
                    'Requesting-User-Uid': userInfo.uid
                }
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || `Failed to ${action} document`);
            }
            window.location.reload();
        } catch (error) {
            console.error(`Error ${action}ing document:`, error);
            alert(`Error: ${error.message}`);
        }
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleAttachmentDownload = async (attachment) => {
        try {
            if (!userInfo) {
                alert("You must be logged in to download attachments");
                return;
            }

            const token = await userInfo.getIdToken();
            const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/attachments/${attachment.id}/download/`, {
                method: "GET",
                headers: {
                    'Auth-Token': `Bearer ${token}`,
                    'Authorization': `${token}`,
                    'Requesting-User-Uid': userInfo.uid
                }
            });

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = window.document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = attachment.file_name;
            window.document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            window.document.body.removeChild(a);
        } catch (error) {
            console.error("Error downloading attachment:", error);
            alert("Failed to download attachment. Please try again.");
        }
    };

    const handleHomeButtonClick = () => { navigate("/") }

    const docTitle = type === 'space' ? document?.name : document?.page_name;

    const hasChildren = type === 'space'
        ? (document?.pages?.length > 0)
        : (document?.child_pages?.length > 0);

    const handleExportMarkdown = () => {
        downloadAsMarkdown(
            document?.content,
            docTitle,
            {
                createdAt: document?.created_at ? formatTimestamp(document.created_at) : '',
                updatedAt: document?.updated_at ? formatTimestamp(document.updated_at) : '',
                views: document?.views
            }
        );
    };

    const handleExportJSON = () => {
        downloadAsJSON({
            id: document?.id,
            type,
            name: docTitle,
            workspace: customDomain,
            content: document?.content,
            views: document?.views,
            created_at: document?.created_at,
            updated_at: document?.updated_at,
            is_public: document?.is_public,
            completed: document?.completed,
            attachment: document?.attachment || null
        }, docTitle);
    };

    const fetchWorkspaceDescendants = async (pagesArray, token, result = [], depth = 0) => {
        if (depth > 4 || !pagesArray || pagesArray.length === 0) return result;
        for (const page of pagesArray) {
            try {
                const response = await fetch(
                    `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${page.id}/`,
                    {
                        headers: {
                            'Auth-Token': `Bearer ${token}`,
                            'Authorization': `${token}`,
                            'Requesting-User-Uid': userInfo.uid,
                            'Requesting-Type': 'view'
                        }
                    }
                );
                if (!response.ok) continue;
                const data = await response.json();
                result.push({ title: data.page_name, data });
                if (data.child_pages && data.child_pages.length > 0) {
                    await fetchWorkspaceDescendants(data.child_pages, token, result, depth + 1);
                }
            } catch (err) {
                console.error(`Failed to fetch workspace child page ${page.id}:`, err);
            }
        }
        return result;
    };

    const handleExportClick = (format) => {
        // PDF always opens the modal: it holds the page options and the
        // progress of the server-side render.
        if (hasChildren || format === 'pdf') {
            setExportScope('current');
            setPdfStatus(null);
            setExportModal({ format });
        } else {
            if (format === 'markdown') handleExportMarkdown();
            else handleExportJSON();
        }
    };

    const handleExportPdf = async () => {
        setExportLoading(true);
        setPdfStatus({ state: 'working', message: 'Generating your PDF…' });
        savePdfOptions(pdfOptions);
        try {
            const token = await userInfo.getIdToken();
            const result = await exportWorkspacePdf({
                token,
                workspaceId: workspace.id,
                documentType: type,
                documentId: document.id,
                title: docTitle,
                includeChildren: hasChildren && exportScope === 'all',
                options: pdfOptions,
            });
            if (result.status === 'queued') {
                setPdfStatus({ state: 'queued', message: "This is a large export, so it's being prepared in the background. We'll notify you when it's ready." });
            } else {
                setExportModal(null);
            }
        } catch (err) {
            console.error('PDF export failed:', err);
            setPdfStatus({ state: 'error', message: err.message });
        } finally {
            setExportLoading(false);
        }
    };

    const handleExportConfirm = async () => {
        const format = exportModal.format;
        if (format === 'pdf') {
            await handleExportPdf();
            return;
        }
        if (exportScope === 'current') {
            if (format === 'markdown') handleExportMarkdown();
            else handleExportJSON();
            setExportModal(null);
            return;
        }
        setExportLoading(true);
        try {
            const token = await userInfo.getIdToken();
            const rootDoc = { title: docTitle, data: document };
            const childrenSource = type === 'space' ? document?.pages : document?.child_pages;
            const descendants = await fetchWorkspaceDescendants(childrenSource || [], token);
            await downloadBulkAsZip([rootDoc, ...descendants], format, docTitle);
        } catch (err) {
            console.error('Export failed:', err);
            alert('Export failed. Please try again.');
        } finally {
            setExportLoading(false);
            setExportModal(null);
        }
    };

    const handleShareClick = () => {
        setIsShareVisible(!isShareVisible)
    }

    const getPublicURL = () => {
        const docName = type === 'space' ? document?.name : document?.page_name;
        return `https://public.bluebird-documentation.com/docs/team_${type}/${id}/${encodeURIComponent(docName)}?workspace_id=${workspace?.id}&doc_id=${id}`;
    }

    const copyToClipboard = () => {
        navigator.clipboard.writeText(getPublicURL());
        alert("Link copied to clipboard!");
    };

    const capitalizeFirstLetter = (val) => {
        return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }

    const handleContentClick = (e) => {
        const chip = e.target.closest('.task-reference');
        if (!chip) return;
        openTaskModal(
            chip.getAttribute('data-task-id'),
            chip.getAttribute('data-board-id'),
            chip.getAttribute('data-task-db-id')
        );
    };

    if (loading) {
    return (
      <div className="DocumentationHomeComponent__coffee-loader-div">
        <div className="coffee-cup-loader">
          <div className="cup">
            <div className="cup-body"></div>
            <div className="cup-handle"></div>
            <div className="cup-base"></div>
            <div className="cup-shadow"></div>
            <div className="steam steam1"></div>
            <div className="steam steam2"></div>
            <div className="steam steam3"></div>
          </div>
        </div>
        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
      </div>
    );
    }

  if (error) {
      return (
          <div 
            className="DocumentationMainPage__div DocViewer DocViewer--ws"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
              <div className="DocumentationMainPage__main-content">
                  <p className="large">Oops! Something went wrong!</p>
                  <p className="medium">{error}</p>
                  <button onClick={handleHomeButtonClick} className="ConflictResolver__button">Home</button>
              </div>
              {enhancedRightClickEnabled && ContextMenuComponent}
          </div>
      );
  }

    if (authResolved && !userInfo) {
        return (
            <div 
                className="DocumentationMainPage__div DocViewer DocViewer--ws"
                onContextMenu={
                    enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                        }
                    : undefined
                }
            >
                <div className="DocumentationMainPage__main-content">
                    <h3>Unauthorized</h3>
                    <p>Please log in to view this document.</p>
                    <button onClick={() => navigate("/login")} className="ConflictResolver__button">Log In</button>
                </div>
                {enhancedRightClickEnabled && ContextMenuComponent}
            </div>
        );
    }

    return (
        <div 
            className="TeamWorkspaceMainPage__div DocViewer DocViewer--ws"
            onContextMenu={
                enhancedRightClickEnabled
                ? (e) => {
                    e.preventDefault();
                    showContextMenu(e.clientX, e.clientY);
                    }
                : undefined
            }
        >
            <WorkspaceSidebar 
                workspaceId={workspace?.id} 
                customDomain={customDomain}
                workspaceTheme={workspaceTheme}
                isSharedWorkspace={true}
            />
            
            <div className="Document__floating-div">
                {userInfo && (
                    <i 
                        className={`fa-${isFavorited ? 'solid' : 'regular'} fa-star Document__icon-padding Document__icon-hover`} 
                        title={isFavorited ? "Remove from favorites" : "Add to favorites"} 
                        onClick={toggleFavorite} 
                        style={{ cursor: "pointer" }}
                    ></i>
                )}
                
                {canPin && (
                    <>
                        <i 
                            className={`fa-solid fa-thumbtack Document__icon-padding Document__icon-hover ${isPinned ? 'Document__icon-pinned' : ''}`} 
                            title={isPinned ? "Unpin document" : "Pin document"} 
                            onClick={togglePin} 
                            style={{ cursor: "pointer", transform: isPinned ? 'rotate(45deg)' : 'none' }}
                        ></i>
                    </>
                )}
                
                <div className="Document__icon-separator"></div>
                
                {document?.is_public && (
                    <i className="fa-solid fa-share Document__icon-padding Document__icon-hover" title="Share" onClick={handleShareClick} style={{ cursor: "pointer", marginLeft: "0" }}></i>
                )}
                
                {canEdit ? (
                    <Link to={`/workspace/${customDomain}/edit/${type}/${id}/${type === 'space' ? document?.name : document?.page_name}`} className="Document__a">
                        <i className="fas fa-edit Document__icon-padding Document__icon-hover" title="Edit" style={{ cursor: "pointer" }}></i>
                    </Link>
                ) : (
                    <i className="fas fa-lock Document__icon-padding" title="Can't Edit" style={{ cursor: "not-allowed" }}></i>
                )}
                
            {canEdit ? (
                    <i className="fas fa-trash Document__icon-padding Document__icon-hover" title="Delete Document" style={{ cursor: "pointer" }} onClick={handleDeleteDocument}></i>
                ) : (
                    <i className="fas fa-lock Document__icon-padding" title="Can't Delete" style={{ cursor: "not-allowed" }}></i>
                )}
                {canEdit && (
                    <i className="fas fa-puzzle-piece Document__icon-padding Document__icon-hover" title="Save as template" style={{ cursor: "pointer" }} onClick={() => setShowSaveAsTemplate(true)}></i>
                )}

                {canPin && (
                    <i
                        className={`fas ${document?.is_archived ? 'fa-box-open' : 'fa-box-archive'} Document__icon-padding Document__icon-hover`}
                        title={document?.is_archived ? "Unarchive" : "Archive"}
                        style={{ cursor: "pointer" }}
                        onClick={handleArchiveDocument}
                    ></i>
                )}

                <div className="Document__icon-separator"></div>
                
                {document?.is_public ? (
                    <i className="fas fa-eye Document__icon-padding" title="Document is Public" style={{ cursor: "pointer" }}></i>
                ) : (
                    <i className="fas fa-eye-slash Document__icon-padding" title="Document is not Public" style={{ cursor: "pointer" }}></i>
                )}
                
                {document?.completed ? (
                    <i className="fa-solid fa-circle-check Document__icon-padding" title="Published"></i>
                ) : (
                    <i className="fa-solid fa-circle-xmark Document__icon-padding" title="Not Published"></i>
                )}
                <div className="Document__icon-separator"></div>
                <i className="fa-solid fa-file-lines Document__icon-padding Document__icon-hover" title="Export as Markdown" style={{ cursor: "pointer" }} onClick={() => handleExportClick('markdown')}></i>
                <i className="fa-solid fa-file-code Document__icon-padding Document__icon-hover" title="Export as JSON" style={{ cursor: "pointer" }} onClick={() => handleExportClick('json')}></i>
                <i className="fa-solid fa-file-pdf Document__icon-padding Document__icon-hover" title="Export as PDF" style={{ cursor: "pointer" }} onClick={() => handleExportClick('pdf')}></i>
                <i className="fa-solid fa-print Document__icon-padding Document__icon-hover" title="Print" style={{ cursor: "pointer" }} onClick={() => window.print()}></i>
            </div>
    
            <SaveAsTemplateDialog
                isOpen={showSaveAsTemplate}
                onClose={() => setShowSaveAsTemplate(false)}
                workspaceId={workspace?.id}
                workspaceRole={workspace?.user_role}
                source={{
                    name: type === 'space' ? document?.name : document?.page_name,
                    description: document?.description || "",
                    content: rawContent,
                    docType: type === 'space' ? "Space" : "Page",
                    hasChildren,
                    loadDescendants: async () => {
                        const token = await userInfo.getIdToken();
                        const childrenSource = type === 'space' ? document?.pages : document?.child_pages;
                        return fetchWorkspaceDescendants(childrenSource || [], token);
                    },
                }}
                onSaved={(saved) => alert(`Template "${saved.name}" saved${saved.scope === 'workspace' ? ' to this workspace' : ' under My Templates'}.`)}
            />
            {isShareVisible && (
                <div className="Document__overlay">
                    <div className="Document__popup">
                        <h2>Share {capitalizeFirstLetter(type)}</h2>
                        <p>To share a link of this single document, you can copy the link below.</p>
                        <div className="Document__shareable-link-container">
                            <p><span role="textbox" aria-label="Shareable link" className="Document__share-link">{getPublicURL()}</span></p>
                            <i
                                className="fa-solid fa-copy Document__copy-icon"
                                onClick={copyToClipboard}
                                title="Copy to clipboard"
                            ></i>
                        </div>
                        <button className="Document__modal-close-button" onClick={handleShareClick}>Close</button>
                    </div>
                </div>
            )}

            {exportModal && (
                <div className="Document__overlay">
                    <div className="Document__popup Document__popup--export">
                        <h2>Export as {exportModal.format === 'markdown' ? 'Markdown' : exportModal.format === 'pdf' ? 'PDF' : 'JSON'}</h2>
                        {exportLoading && exportModal.format !== 'pdf' ? (
                            <p style={{ padding: '20px' }}>Fetching child documents...</p>
                        ) : pdfStatus && pdfStatus.state === 'queued' ? (
                            <>
                                <p>{pdfStatus.message}</p>
                                <button className="Document__modal-submit-button" onClick={() => setExportModal(null)}>Close</button>
                            </>
                        ) : (
                            <>
                                {hasChildren && (
                                    <>
                                        <p>What would you like to export?</p>
                                        <div className="Document__export-scope">
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="exportScope"
                                                    value="current"
                                                    checked={exportScope === 'current'}
                                                    onChange={() => setExportScope('current')}
                                                />
                                                This document only
                                            </label>
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="exportScope"
                                                    value="all"
                                                    checked={exportScope === 'all'}
                                                    onChange={() => setExportScope('all')}
                                                />
                                                This document + all children <span className="Document__export-hint">{exportModal.format === 'pdf' ? '(one combined PDF)' : '(downloaded as .zip)'}</span>
                                            </label>
                                        </div>
                                    </>
                                )}
                                {exportModal.format === 'pdf' && (
                                    <PdfExportOptions
                                        options={pdfOptions}
                                        onChange={setPdfOptions}
                                        combined={hasChildren && exportScope === 'all'}
                                        status={pdfStatus}
                                    />
                                )}
                                <button className="Document__modal-close-button" onClick={() => setExportModal(null)} disabled={exportLoading}>Cancel</button>
                                <button className="Document__modal-submit-button" onClick={handleExportConfirm} disabled={exportLoading}>
                                    {exportLoading ? 'Generating…' : exportModal.format === 'pdf' ? 'Download PDF' : 'Export'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            <div className={`TeamWorkspaceMainPage__main-content${userInfo && workspace.id ? " TeamWorkspaceMainPage__main-content--with-comments" : ""}`}>
                {/* BBD-138: with comments docked below, the document scrolls inside this region */}
                <div className="Document__scroll">
                <div className="Document__div">
                    <h2 className="Document__div-title">{type === 'space' ? document?.name : document?.page_name}</h2>
                    {/* Shown on paper only (Ctrl+P, BBD-120) */}
                    {document?.description && document.description !== "null" && (
                        <p className="Document__print-description">{document.description}</p>
                    )}
                    
                    {document?.attachment && (
                        <div className="Document__attachments-section">
                            <div className="Document__attachments-list">
                                <div className="Document__attachment-item">
                                    <div className="Document__attachment-info">
                                        <div className="Document__attachment-name-row">
                                            <i className="fas fa-file-archive Document__attachment-icon"></i>
                                            <span className="Document__attachment-name">
                                                {document.attachment.file_name}
                                            </span>
                                        </div>
                                        <div className="Document__attachment-details">
                                            <span className="Document__attachment-detail-item">
                                                <i className="fas fa-weight-hanging"></i>
                                                {formatFileSize(document.attachment.file_size)}
                                            </span>
                                            <span className="Document__attachment-detail-item">
                                                <i className="fas fa-download"></i>
                                                {document.attachment.download_count || 0} downloads
                                            </span>
                                            <span className="Document__attachment-detail-item">
                                                <i className="fas fa-calendar"></i>
                                                {formatTimestamp(document.attachment.created_at)}
                                            </span>
                                        </div>
                                        {document.attachment.description && (
                                            <div className="Document__attachment-description">
                                                {document.attachment.description}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleAttachmentDownload(document.attachment)}
                                        className="Document__attachment-download-btn"
                                        title={`Download ${document.attachment.file_name}`}
                                    >
                                        <i className="fas fa-download"></i>
                                        Download
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {type === 'space' && workspace?.id && document?.show_table_of_contents !== false && (
                        <SpaceTableOfContents
                            variant="workspace"
                            workspaceId={workspace.id}
                            spaceId={id}
                            customDomain={customDomain}
                        />
                    )}

                    <p className="extra-small Document__document-views-text">
                        <span className="semi-bold">Views: </span> <i>{document?.views || 0}</i>
                    </p>
    
                    <p className="extra-small" style={{ paddingTop: "30px" }}>
                        <span className="semi-bold">Created At:</span> <i>{document?.created_at ? formatTimestamp(document.created_at) : 'N/A'}</i>
                    </p>
                    <p className="extra-small" style={{ paddingBottom: "30px" }}>
                        <span className="semi-bold">Last Updated At:</span> <i>{document?.updated_at ? formatTimestamp(document.updated_at) : 'N/A'}</i>
                    </p>
    
                    <div
                        className="ql-editor ql-editor-styling"
                        onClick={handleContentClick}
                        dangerouslySetInnerHTML={{ __html: document?.content || 'No content available' }}
                    />
                </div>
                </div>

                {userInfo && workspace.id && (
                    <WorkspaceComments
                        workspaceId={workspace?.id}
                        documentType={type}
                        documentId={id}
                        userInfo={userInfo}
                        workspaceTheme={workspaceTheme}
                        userRole={workspace?.user_role}
                    />
                )}
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}

            {activeTask && (
                <TaskDetailModal
                    boardTaskId={activeTask.boardTaskId}
                    boardId={activeTask.boardId}
                    taskDbId={activeTask.taskDbId}
                    onClose={closeTaskModal}
                />
            )}
        </div>
    );
}

export default WorkspaceDocument